import { useState, useEffect } from 'react';
import { 
  FolderKanban, 
  Plus, 
  Edit2, 
  Trash2, 
  ChevronRight,
  ChevronDown,
  AlertCircle,
  Check,
  X,
  GripVertical
} from 'lucide-react';
import { api } from '../../utils/api';

// Parent category options - these map to QBO expense accounts
const PARENT_CATEGORIES = [
  { value: 'ENTERTAINMENT', label: 'Entertainment', expenseAccount: 'Entertainment Expenses' },
  { value: 'PRODUCTION', label: 'Production', expenseAccount: 'Production Expenses' },
  { value: 'RENTALS', label: 'Rentals', expenseAccount: 'Rental Expenses' },
  { value: 'STAFFING', label: 'Staffing', expenseAccount: 'Staffing Expenses' },
  { value: 'CATERING', label: 'Catering', expenseAccount: 'Catering Expenses' },
  { value: 'TRANSPORTATION', label: 'Transportation', expenseAccount: 'Transportation Expenses' },
  { value: 'ADMIN', label: 'Admin', expenseAccount: 'Administrative Expenses' },
];

export default function EventCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [expandedParents, setExpandedParents] = useState(new Set(PARENT_CATEGORIES.map(p => p.value)));
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    parentCategory: 'ENTERTAINMENT',
    qboProductService: '',
    qboExpenseAccount: '',
    description: '',
    active: true
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/event-categories');
      setCategories(Array.isArray(response) ? response : response.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Auto-set expense account based on parent category
      const parent = PARENT_CATEGORIES.find(p => p.value === formData.parentCategory);
      const dataToSend = {
        ...formData,
        qboExpenseAccount: formData.qboExpenseAccount || parent?.expenseAccount || ''
      };

      if (editingCategory) {
        await api.put(`/event-categories/${editingCategory.id}`, dataToSend);
      } else {
        await api.post('/event-categories', dataToSend);
      }
      
      setShowModal(false);
      setEditingCategory(null);
      resetForm();
      fetchCategories();
    } catch (err) {
      console.error('Error saving category:', err);
      alert(err.response?.data?.error || 'Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      parentCategory: category.parentCategory || 'ENTERTAINMENT',
      qboProductService: category.qboProductService || '',
      qboExpenseAccount: category.qboExpenseAccount || '',
      description: category.description || '',
      active: category.active !== false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/event-categories/${id}`);
      setDeleteConfirm(null);
      fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      alert(err.response?.data?.error || 'Failed to delete category');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      parentCategory: 'ENTERTAINMENT',
      qboProductService: '',
      qboExpenseAccount: '',
      description: '',
      active: true
    });
  };

  const openAddModal = (parentCategory = 'ENTERTAINMENT') => {
    setEditingCategory(null);
    const parent = PARENT_CATEGORIES.find(p => p.value === parentCategory);
    setFormData({
      name: '',
      parentCategory: parentCategory,
      qboProductService: '',
      qboExpenseAccount: parent?.expenseAccount || '',
      description: '',
      active: true
    });
    setShowModal(true);
  };

  const toggleParent = (parentValue) => {
    const newExpanded = new Set(expandedParents);
    if (newExpanded.has(parentValue)) {
      newExpanded.delete(parentValue);
    } else {
      newExpanded.add(parentValue);
    }
    setExpandedParents(newExpanded);
  };

  // Group categories by parent
  const groupedCategories = PARENT_CATEGORIES.reduce((acc, parent) => {
    acc[parent.value] = categories.filter(c => c.parentCategory === parent.value);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FolderKanban className="w-7 h-7" />
            Manage Categories
          </h1>
          <p className="text-gray-600 mt-1">
            Line item categories for quotes and invoicing with QuickBooks mapping
          </p>
        </div>
        <button
          onClick={() => openAddModal()}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Category
        </button>
      </div>

      {/* Warning box */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-amber-800">QuickBooks Mapping</h3>
            <p className="text-sm text-amber-700 mt-1">
              <strong>QBO Product/Service</strong> must match exactly as it appears in QuickBooks → Products and Services. 
              This is used for Invoice line items (what clients pay you).
            </p>
            <p className="text-sm text-amber-700 mt-1">
              <strong>QBO Expense Account</strong> must match exactly as it appears in QuickBooks → Chart of Accounts.
              This is used for Bills (what you pay vendors).
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Categories grouped by parent */}
      <div className="space-y-2">
        {PARENT_CATEGORIES.map(parent => {
          const children = groupedCategories[parent.value] || [];
          const isExpanded = expandedParents.has(parent.value);
          
          return (
            <div key={parent.value} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Parent header */}
              <div 
                className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                onClick={() => toggleParent(parent.value)}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="font-semibold text-gray-900">{parent.label}</span>
                  <span className="text-sm text-gray-500">({children.length})</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">
                    Expense Account: <span className="font-medium">{parent.expenseAccount}</span>
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openAddModal(parent.value);
                    }}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add
                  </button>
                </div>
              </div>

              {/* Children */}
              {isExpanded && (
                <div className="divide-y divide-gray-100">
                  {children.length === 0 ? (
                    <div className="px-4 py-6 text-center text-gray-500 text-sm">
                      No categories yet. Click "Add" to create one.
                    </div>
                  ) : (
                    children.map(category => (
                      <div 
                        key={category.id} 
                        className={`px-4 py-3 flex items-center justify-between hover:bg-gray-50 ${!category.active ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <GripVertical className="w-4 h-4 text-gray-300" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{category.name}</span>
                              {!category.active && (
                                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                                  Inactive
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-4 gap-y-1">
                              {category.qboProductService && (
                                <span>
                                  <span className="text-gray-400">Invoice Item:</span> {category.qboProductService}
                                </span>
                              )}
                              {category.qboExpenseAccount && (
                                <span>
                                  <span className="text-gray-400">Expense:</span> {category.qboExpenseAccount}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => handleEdit(category)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(category)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Uncategorized - categories without a parent */}
      {categories.filter(c => !PARENT_CATEGORIES.some(p => p.value === c.parentCategory)).length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-red-50 border-b border-red-200">
            <span className="font-semibold text-red-800">⚠️ Uncategorized (needs parent)</span>
          </div>
          <div className="divide-y divide-gray-100">
            {categories
              .filter(c => !PARENT_CATEGORIES.some(p => p.value === c.parentCategory))
              .map(category => (
                <div 
                  key={category.id} 
                  className="px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                >
                  <span className="font-medium text-gray-900">{category.name}</span>
                  <button
                    onClick={() => handleEdit(category)}
                    className="text-blue-600 hover:text-blue-700 text-sm"
                  >
                    Edit to assign parent
                  </button>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingCategory ? 'Edit Category' : 'Add Category'}
              </h2>
              <button 
                onClick={() => {
                  setShowModal(false);
                  setEditingCategory(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Category Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input"
                  placeholder="e.g., Live Band, DJ Services, Lighting"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Internal name for this category
                </p>
              </div>

              {/* Parent Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Category *
                </label>
                <select
                  value={formData.parentCategory}
                  onChange={(e) => {
                    const parent = PARENT_CATEGORIES.find(p => p.value === e.target.value);
                    setFormData({ 
                      ...formData, 
                      parentCategory: e.target.value,
                      qboExpenseAccount: parent?.expenseAccount || formData.qboExpenseAccount
                    });
                  }}
                  className="input"
                  required
                >
                  {PARENT_CATEGORIES.map(parent => (
                    <option key={parent.value} value={parent.value}>
                      {parent.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Groups related categories together
                </p>
              </div>

              {/* QBO Product/Service */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  QBO Product/Service
                </label>
                <input
                  type="text"
                  value={formData.qboProductService}
                  onChange={(e) => setFormData({ ...formData, qboProductService: e.target.value })}
                  className="input"
                  placeholder="e.g., Live Band"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must match exactly in QuickBooks → Products and Services. Used for invoices.
                </p>
              </div>

              {/* QBO Expense Account */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  QBO Expense Account
                </label>
                <input
                  type="text"
                  value={formData.qboExpenseAccount}
                  onChange={(e) => setFormData({ ...formData, qboExpenseAccount: e.target.value })}
                  className="input"
                  placeholder="e.g., Entertainment Expenses"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must match exactly in QuickBooks → Chart of Accounts. Used for bills.
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input"
                  rows={2}
                  placeholder="Optional notes about this category"
                />
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                />
                <label htmlFor="active" className="text-sm text-gray-700">
                  Active (show in dropdowns)
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategory(null);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingCategory ? 'Save Changes' : 'Add Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Category?</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? 
              This may affect existing quotes and vendors using this category.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                className="btn-primary bg-red-600 hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 
